import { CmmsConsent } from 'constants/cmmsConsent'
import { useCallback, useMemo } from 'react'
import { RequestError, fetchWithTimeout } from 'utils'
import { useFetchCoreAuthToken } from '../useCoreAuthService'
import { useServiceCountryUrl } from '../useServiceCountryUrl'

const ApiPathname = {
  CUSTOMER_CONSENTS: '/customer-consent/:customerGid',
} as const

type GetConsentsQuery = {
  customerGid: string
}

const useCoreConsentManagementService = () => {
  const { url: baseUrl } = useServiceCountryUrl('core-consent-management')
  const { getToken } = useFetchCoreAuthToken()

  const getConsents = useCallback(
    async (query: GetConsentsQuery) => {
      const pathname = ApiPathname.CUSTOMER_CONSENTS.replace(':customerGid', query.customerGid)
      const url = new URL(pathname, baseUrl)
      const token = await getToken()

      const response = await fetchWithTimeout(url.href, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      })

      if (!response.ok) {
        throw new RequestError(`Failed to fetch consents: ${response.status}`, response.status)
      }

      const data = await response.json()

      return data as CmmsConsent[]
    },
    [baseUrl, getToken]
  )

  return useMemo(() => ({ getConsents }), [getConsents])
}

export { useCoreConsentManagementService }
